import type { HttpTransport } from '@mangovoice/api-client'
import type { PaginatedApiResult, UserRoles } from '@mangovoice/types'
import type { components } from '@mangovoice/types/generated/mango-api'

import { stringifyParams } from './utils'

type User = components['schemas']['User']
type UserDetailV2 = components['schemas']['UserDetailV2']

export interface UsersListParams {
  pbx_id?: number
  paginate?: boolean
  limit?: number
  offset?: number
  search?: string
  ordering?: string
}

export type UserListItem = UserDetailV2

export function createUsersResource(transport: HttpTransport) {
  return {
    /** List users (select endpoint for dropdowns) */
    listSelect: (params?: UsersListParams) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<User>>(
        qs ? `/users/select/?${qs}` : '/users/select/',
      )
    },

    /** List users via v2 API */
    getPbxUsers: (params?: UsersListParams) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<User>>(
        qs ? `/app/v2/users/?${qs}` : '/app/v2/users/',
      )
    },

    /** Get a single user by ID */
    getUser: (userId: number) =>
      transport.get<UserListItem>(`/app/v2/users/${userId}/`),

    /** Create a new user */
    createUser: (data: Record<string, unknown>) =>
      transport.post<UserListItem>('/app/v2/users/', data),

    /** Update a user */
    updateUser: (
      userId: number,
      data: { first_name?: string; last_name?: string; [key: string]: unknown },
    ) => transport.patch<UserListItem>(`/app/v2/users/${userId}/`, data),

    /** Get user roles */
    getUserRoles: (userId: number) =>
      transport.get<UserRoles>(`/users/${userId}/roles/`),

    /** Get user PBXes */
    getUserPbxes: (userId: number) =>
      transport.get<unknown>(`/app/v2/users/${userId}/pbxes/`),

    /** Get available roles */
    getAvailableRoles: () => transport.get<unknown>('/app/v1/roles/'),

    /** List users for a specific PBX */
    listByPbx: (
      pbxId: number,
      params?: { limit?: number; offset?: number; search?: string },
    ) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<User>>(
        qs
          ? `/app/v1/pbxes/${pbxId}/users/?${qs}`
          : `/app/v1/pbxes/${pbxId}/users/`,
      )
    },

    /** Add user to a PBX */
    addToPbx: (pbxId: number, data: Record<string, unknown>) =>
      transport.post<unknown>(`/app/v1/pbxes/${pbxId}/users/`, data),

    /** Update user's PBX membership */
    updatePbxUser: (
      pbxId: number,
      userId: number,
      data: Record<string, unknown>,
    ) =>
      transport.patch<unknown>(`/app/v1/pbxes/${pbxId}/users/${userId}/`, data),

    /** Remove user from a PBX */
    removeFromPbx: (pbxId: number, userId: number) =>
      transport.delete<void>(`/app/v1/pbxes/${pbxId}/users/${userId}/`),

    /** Reset user password */
    resetPassword: (userId: number) =>
      transport.get<unknown>(`/app/v2/users/${userId}/reset-password/`),
  }
}
